'use client';

import Member from '@/components/Product/Member';
import { useStore } from '@/providers/app-store-provider';
import { ELensColor, EPrescriptionType } from '@/utils/enums/goods';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { useTranslations } from '@/i18n/use-translations';
import { useLensProcessStore } from '../store/lens-process-store';
import lensInfoClassName from '../style/lens-info';
import CoatingMemberTag from './CoatingMemberTag';
import LensIndexMemberTag from './LensIndexMemberTag';
import LensTypeMemberTag from './LensTypeMemberTag';

export default function LensInfo({ isFrameProcess }: { isFrameProcess: boolean }) {
  const t = useTranslations('lens-process');
  const userInfo = useStore((state) => state.userInfo);
  const currency = useStore((state) => state.currency);
  const waiMode = useStore((state) => state.waiMode);
  const {
    isRedeem,
    // 是否是无处方类型（包括无处方、太阳镜无处方）
    isNonPrescriptionType,
    // 商品基本信息
    lensGoodsItem,
    // 处方类型
    prescriptionType,
    presTypeName,
    // 价格相关
    goodsFramePriceInfo,
    lensTypePriceInfo,
    lensIndexPriceInfo,
    coatingPriceInfo,
    // 选中项
    selectedLensIndexItem,
    selectedCoating,
    selectedColorItem,
    selectedAdvancedLensIndexItem,
    selectedLensType,
    selectedBrand,
  } = useLensProcessStore();

  return (
    <div className={lensInfoClassName.container}>
      {/* Frame */}
      <div className={`${lensInfoClassName.sectionTitle}`}>{t('frame')}</div>
      <div className={lensInfoClassName.contentRow}>
        <div>{lensGoodsItem?.product?.short_name}</div>
        <div className={lensInfoClassName.priceContainer}>
          {!isRedeem && userInfo.is_member === 1 && (
            <Member
              level={userInfo.member_level}
              className={lensInfoClassName.memberTag}
              productData={lensGoodsItem?.product as any}
            />
          )}
          {Number(goodsFramePriceInfo.sell_price) !== Number(goodsFramePriceInfo.price) && (
            <span className={lensInfoClassName.originalPrice(waiMode)}>
              {formatPriceNumberWithSymbol(goodsFramePriceInfo.price, currency)}
            </span>
          )}
          <span className={lensInfoClassName.currentPrice}>
            {formatPriceNumberWithSymbol(goodsFramePriceInfo.sell_price, currency)}
          </span>
        </div>
      </div>
      {/* 镜框颜色 */}
      <div className={lensInfoClassName.colorDescription}>—{lensGoodsItem?.product?.color}</div>

      {/* Prescription */}
      {!isFrameProcess &&
        prescriptionType &&
        prescriptionType !== EPrescriptionType.READING_GLASSES && (
          <>
            <div className={lensInfoClassName.sectionTitle}>{t('prescription')}</div>
            <div className={lensInfoClassName.contentRow}>{presTypeName}</div>
          </>
        )}

      {/* Lens */}
      {(selectedAdvancedLensIndexItem ||
        selectedLensType?.merge_brand === 2 ||
        (selectedLensType?.merge_brand === 1 && selectedBrand?.brand_name)) &&
        prescriptionType !== EPrescriptionType.FRAME_ONLY && (
          <>
            <div className={lensInfoClassName.sectionTitle}>{t('lens')}</div>
            <div className={lensInfoClassName.contentRow}>
              <div>
                {selectedAdvancedLensIndexItem?.lens_type_name ||
                  selectedBrand?.brand_name ||
                  selectedLensType?.lens_name}
              </div>
              <div className={lensInfoClassName.priceContainer}>
                <LensTypeMemberTag
                  colorItem={selectedAdvancedLensIndexItem || selectedColorItem}
                  hasIndexPrice={isFrameProcess || isNonPrescriptionType}
                />
                {lensTypePriceInfo?.price !== lensTypePriceInfo?.sell_price && (
                  <span className={lensInfoClassName.originalPrice(waiMode)}>
                    {formatPriceNumberWithSymbol(lensTypePriceInfo.price, currency)}
                  </span>
                )}
                {lensTypePriceInfo?.sell_price && (
                  <span className={lensInfoClassName.currentPrice}>
                    {formatPriceNumberWithSymbol(lensTypePriceInfo.sell_price, currency)}
                  </span>
                )}
              </div>
            </div>
            {/* 镜片颜色 */}
            {selectedColorItem && selectedColorItem.lens_color !== ELensColor.NON && (
              <div className={lensInfoClassName.colorDescription}>
                —{selectedColorItem.color_name}
              </div>
            )}
          </>
        )}

      {/* 折射率-品牌 */}
      {selectedLensIndexItem && lensIndexPriceInfo && (
        <>
          <div className={`${lensInfoClassName.contentRow} mt-[16px] xl:mt-[24px]`}>
            <div>{selectedLensIndexItem?.index_name}</div>
            <div className={lensInfoClassName.priceContainer}>
              <LensIndexMemberTag
                lensIndexItem={selectedAdvancedLensIndexItem || selectedLensIndexItem}
              />
              {lensIndexPriceInfo.price !== lensIndexPriceInfo.sell_price && (
                <span className={lensInfoClassName.originalPrice(waiMode)}>
                  {formatPriceNumberWithSymbol(lensIndexPriceInfo.price, currency)}
                </span>
              )}
              <span className={lensInfoClassName.currentPrice}>
                {formatPriceNumberWithSymbol(lensIndexPriceInfo.sell_price, currency)}
              </span>
            </div>
          </div>
        </>
      )}

      {/* Coating */}
      {selectedCoating && (
        <>
          <div className={lensInfoClassName.sectionTitle}>{t('coating')}</div>
          <div className={lensInfoClassName.contentRow}>
            <div>{selectedCoating?.name}</div>
            {coatingPriceInfo && (
              <div className={lensInfoClassName.priceContainer}>
                <CoatingMemberTag coatingItem={selectedCoating} />
                {coatingPriceInfo?.price !== coatingPriceInfo?.sell_price && (
                  <span className={lensInfoClassName.originalPrice(waiMode)}>
                    {formatPriceNumberWithSymbol(coatingPriceInfo.price, currency)}
                  </span>
                )}
                <span className={lensInfoClassName.currentPrice}>
                  {formatPriceNumberWithSymbol(coatingPriceInfo.sell_price, currency)}
                </span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
